import { Head, Link } from "@inertiajs/react"
import { FormattedMessage, useIntl, type IntlShape } from "react-intl"
import { AlertTriangle, CheckCircle2, CircleDashed, ClipboardList, Layers, ListChecks, MinusCircle, Repeat } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { ScoreTrendChart, type TrendPoint } from "@/components/dashboard/ScoreTrendChart"
import { Button } from "@/components/ui/button"
import { StatGrid } from "@/components/blocks/StatGrid"
import { TableCard } from "@/components/blocks/TableCard"
import { Badge } from "@/components/catalyst/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/catalyst/table"
import { useFormat } from "@/lib/format"
import { cn } from "@/lib/utils"

type Level = "strong" | "okay" | "needs_work" | "not_enough_data"

type Strength = {
  id: number
  name: string
  weight: number | null
  total: number
  correct: number
  percent: number | null
  level: Level
}

type StudyLink = { id: number; title: string; tagged: number }

type DashboardProps = {
  program_access: boolean
  exam: { version_name: string | null; date: string | null; days_left: number | null }
  todos: {
    due_count: number
    unfinished_exam: { id: number; title: string; answered: number; question_count: number; time_remaining_seconds: number } | null
  }
  strengths: Strength[]
  study_next: {
    basis: "weakest" | "blueprint"
    areas: { id: number; name: string; percent: number | null; level: Level; decks: StudyLink[]; quizzes: StudyLink[] }[]
  }
  exam_trend: TrendPoint[]
  recent: { id: number; title: string; kind: "quiz" | "quiz_retry" | "practice_exam" | "case_practice"; score: number | null; date: string; href: string }[]
  activity: { cards_reviewed: number; questions_answered: number }
  thresholds: { strong: number; okay: number; min_answers: number; window_days: number }
}

// The bar and icon carry the level's color; its label stays in text ink so it's always legible.
const LEVELS: Record<Level, { label: { id: string }; icon: typeof CheckCircle2; bar: string; iconColor: string }> = {
  strong: { label: { id: "dashboard.level_strong" }, icon: CheckCircle2, bar: "bg-chart-strong", iconColor: "text-chart-strong" },
  okay: { label: { id: "dashboard.level_okay" }, icon: MinusCircle, bar: "bg-chart-okay", iconColor: "text-chart-okay" },
  needs_work: { label: { id: "dashboard.level_needs_work" }, icon: AlertTriangle, bar: "bg-chart-weak", iconColor: "text-chart-weak" },
  not_enough_data: { label: { id: "dashboard.level_no_data" }, icon: CircleDashed, bar: "", iconColor: "text-ink-muted" },
}

const SHORT_DATE = { month: "short", day: "numeric" } as const

const KINDS = {
  quiz: { id: "dashboard.kind_quiz" },
  quiz_retry: { id: "dashboard.kind_quiz_retry" },
  practice_exam: { id: "dashboard.kind_practice_exam" },
  case_practice: { id: "dashboard.kind_case_practice" },
}

export default function Dashboard(props: DashboardProps) {
  const intl = useIntl()
  const format = useFormat()
  const description = intl.formatMessage({ id: "dashboard.meta" })
  const title = intl.formatMessage({ id: "dashboard.title" })
  const access = props.program_access

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell>
        <h1>{title}</h1>

        <StatGrid
          className="mt-6"
          columns={access ? 4 : 3}
          stats={[
            ...(access ? [daysLeftStat(props.exam, intl, format)] : []),
            { name: intl.formatMessage({ id: "dashboard.stat_cards_due" }), value: format.number(props.todos.due_count) },
            { name: intl.formatMessage({ id: "dashboard.stat_cards_reviewed" }), value: format.number(props.activity.cards_reviewed), change: intl.formatMessage({ id: "dashboard.last_7_days" }), trend: "flat" as const },
            { name: intl.formatMessage({ id: "dashboard.stat_questions_answered" }), value: format.number(props.activity.questions_answered), change: intl.formatMessage({ id: "dashboard.last_7_days" }), trend: "flat" as const },
          ]}
        />
        {access && <ExamLine exam={props.exam} />}

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <Todos todos={props.todos} />
          {!access && <UnlockCard />}
        </div>

        {access && (
          <>
            <section className="mt-10">
              <h2>{intl.formatMessage({ id: "dashboard.strength_title" })}</h2>
              <p className="mt-1 text-sm text-ink-muted">
                {intl.formatMessage(
                  { id: "dashboard.strength_hint" },
                  {
                    days: props.thresholds.window_days,
                    strong: props.thresholds.strong,
                    okay: props.thresholds.okay,
                    strong_max: props.thresholds.strong - 1,
                  },
                )}
              </p>
              <Strengths strengths={props.strengths} minAnswers={props.thresholds.min_answers} />
            </section>

            <StudyNext studyNext={props.study_next} />
          </>
        )}

        {access && (
        <section className="mt-10">
          <h2>{intl.formatMessage({ id: "dashboard.scores_title" })}</h2>
          {props.exam_trend.length === 0 ? (
            <p className="mt-2">
              <FormattedMessage
                id="dashboard.scores_empty"
                values={{ link: (chunks) => <Link href="/practice_exams">{chunks}</Link> }}
              />
            </p>
          ) : (
            <div className="mt-4 rounded-xl border border-hairline bg-page p-4">
              <ScoreTrendChart points={props.exam_trend} />
              <details className="mt-3">
                <summary className="cursor-pointer text-sm text-ink-muted">{intl.formatMessage({ id: "dashboard.show_table" })}</summary>
                <table className="mt-2 w-full text-sm">
                  <thead>
                    <tr className="border-b border-hairline text-left text-ink-muted">
                      <th className="py-1.5 pr-4 font-medium">{intl.formatMessage({ id: "dashboard.col_date" })}</th>
                      <th className="py-1.5 pr-4 font-medium">{intl.formatMessage({ id: "dashboard.col_exam" })}</th>
                      <th className="py-1.5 text-right font-medium">{intl.formatMessage({ id: "dashboard.col_score" })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {props.exam_trend.map((p) => (
                      <tr key={p.id} className="border-b border-hairline last:border-0">
                        <td className="py-1.5 pr-4">{format.date(p.date, SHORT_DATE)}</td>
                        <td className="py-1.5 pr-4">{p.title}</td>
                        <td className="py-1.5 text-right tabular-nums">{p.score}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            </div>
          )}
        </section>
        )}

        <section className="mt-10">
          <h2>{intl.formatMessage({ id: "dashboard.recent_title" })}</h2>
          {props.recent.length === 0 ? (
            <p className="mt-2">{intl.formatMessage({ id: "dashboard.recent_empty" })}</p>
          ) : (
            <div className="mt-4">
              <TableCard>
                <Table bleed rowLinks className="[--gutter:--spacing(5)] [&_tbody_tr:last-child_td]:border-b-0">
                  <TableHead>
                    <TableRow>
                      <TableHeader>{intl.formatMessage({ id: "dashboard.col_title" })}</TableHeader>
                      <TableHeader className="hidden sm:table-cell">{intl.formatMessage({ id: "dashboard.col_type" })}</TableHeader>
                      <TableHeader className="hidden sm:table-cell">{intl.formatMessage({ id: "dashboard.col_date" })}</TableHeader>
                      <TableHeader className="text-right">{intl.formatMessage({ id: "dashboard.col_score" })}</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {props.recent.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell>
                          <Link href={r.href} className="font-medium text-ink-display no-underline after:absolute after:inset-0 hover:text-ink-display">
                            {r.title}
                          </Link>
                        </TableCell>
                        <TableCell className="hidden text-ink-muted sm:table-cell">{intl.formatMessage(KINDS[r.kind])}</TableCell>
                        <TableCell className="hidden text-ink-muted sm:table-cell">{format.date(r.date, SHORT_DATE)}</TableCell>
                        <TableCell className="text-right">
                          {r.score == null ? (
                            <span className="text-ink-muted">—</span>
                          ) : (
                            <Badge color={r.score >= props.thresholds.strong ? "lime" : r.score >= props.thresholds.okay ? "amber" : "red"}>
                              {format.number(r.score / 100, { style: "percent" })}
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableCard>
            </div>
          )}
        </section>
      </AppShell>
    </>
  )
}

function UnlockCard() {
  const intl = useIntl()
  return (
    <div className="callout">
      <div className="text-sm text-ink-muted">{intl.formatMessage({ id: "dashboard.unlock_label" })}</div>
      <p className="mt-2">{intl.formatMessage({ id: "dashboard.unlock_body" })}</p>
      <Button asChild variant="secondary" size="sm" className="mt-3">
        <Link href="/profile/programs">{intl.formatMessage({ id: "dashboard.unlock_cta" })}</Link>
      </Button>
    </div>
  )
}

function daysLeftStat(exam: DashboardProps["exam"], intl: IntlShape, format: ReturnType<typeof useFormat>) {
  const name = intl.formatMessage({ id: "dashboard.stat_days_left" })
  const days = exam.days_left
  if (days === null) return { name, value: "—", change: intl.formatMessage({ id: "dashboard.stat_not_set" }), trend: "flat" as const }
  const change = exam.date ? format.date(exam.date) : ""
  if (days < 0) return { name, value: intl.formatMessage({ id: "dashboard.stat_passed" }), change, trend: "flat" as const }
  return { name, value: days === 0 ? intl.formatMessage({ id: "dashboard.stat_today" }) : format.number(days), change, trend: "flat" as const }
}

function ExamLine({ exam }: { exam: DashboardProps["exam"] }) {
  const intl = useIntl()
  const unset = exam.days_left === null
  const passed = exam.days_left !== null && exam.days_left < 0
  return (
    <p className="mt-3 text-sm text-ink-muted">
      {exam.version_name}
      {unset && ` · ${intl.formatMessage({ id: "dashboard.exam_unset" })}`}
      {passed && ` · ${intl.formatMessage({ id: "dashboard.exam_passed" })}`}
      {" · "}
      <Link href="/settings">{unset ? intl.formatMessage({ id: "dashboard.set_date" }) : intl.formatMessage({ id: "dashboard.change" })}</Link>
    </p>
  )
}

function Todos({ todos }: { todos: DashboardProps["todos"] }) {
  const intl = useIntl()
  const format = useFormat()
  const exam = todos.unfinished_exam
  const nothing = todos.due_count === 0 && !exam
  return (
    <div className="callout">
      <div className="text-sm text-ink-muted">{intl.formatMessage({ id: "dashboard.todos_label" })}</div>
      {nothing ? (
        <p className="mt-2">{intl.formatMessage({ id: "dashboard.all_caught_up" })}</p>
      ) : (
        <ul className="mt-2 space-y-3 p-0">
          {todos.due_count > 0 && (
            <li className="flex list-none flex-wrap items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <Layers className="h-4 w-4 text-ink-muted" />{" "}
                {intl.formatMessage({ id: "dashboard.cards_due" }, { count: todos.due_count })}
              </span>
              <Button asChild size="sm">
                <Link href="/review">
                  <Repeat className="h-4 w-4" /> {intl.formatMessage({ id: "dashboard.review" })}
                </Link>
              </Button>
            </li>
          )}
          {exam && (
            <li className="flex list-none flex-wrap items-center justify-between gap-2">
              <span className="flex min-w-0 items-center gap-2">
                <ClipboardList className="h-4 w-4 shrink-0 text-ink-muted" />
                <span className="min-w-0">
                  {intl.formatMessage(
                    { id: "dashboard.exam_progress" },
                    {
                      title: exam.title,
                      answered: exam.answered,
                      total: exam.question_count,
                      remaining: format.duration(exam.time_remaining_seconds),
                    },
                  )}
                </span>
              </span>
              <Button asChild size="sm" variant="secondary">
                <Link href={`/exam_attempts/${exam.id}`}>{intl.formatMessage({ id: "dashboard.resume" })}</Link>
              </Button>
            </li>
          )}
        </ul>
      )}
    </div>
  )
}

function Strengths({ strengths, minAnswers }: { strengths: Strength[]; minAnswers: number }) {
  const intl = useIntl()
  if (strengths.length === 0) return null
  return (
    <ul className="mt-4 divide-y divide-hairline overflow-hidden rounded-xl border border-hairline bg-page p-0">
      {strengths.map((s) => {
        const level = LEVELS[s.level]
        const Icon = level.icon
        const measured = s.level !== "not_enough_data"
        return (
          <li key={s.id} className="list-none px-4 py-3">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <span className="min-w-0 text-sm text-ink-display">{s.name}</span>
              <span className="inline-flex items-center gap-1.5 text-sm text-ink-display">
                <Icon className={cn("h-4 w-4", level.iconColor)} aria-hidden />
                {intl.formatMessage(level.label)}
                <span className="text-ink-muted">
                  {" · "}
                  {measured
                    ? intl.formatMessage({ id: "dashboard.strength_measured" }, { percent: s.percent, total: s.total })
                    : intl.formatMessage({ id: "dashboard.strength_pending" }, { total: s.total, min: minAnswers })}
                </span>
              </span>
            </div>
            <div
              className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface"
              role="meter"
              aria-label={
                measured
                  ? intl.formatMessage({ id: "dashboard.meter_measured" }, { name: s.name, percent: s.percent, level: intl.formatMessage(level.label).toLocaleLowerCase(intl.locale) })
                  : intl.formatMessage({ id: "dashboard.meter_pending" }, { name: s.name })
              }
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={s.percent ?? 0}
            >
              {measured && <div className={cn("h-full rounded-full", level.bar)} style={{ width: `${s.percent}%` }} />}
            </div>
          </li>
        )
      })}
    </ul>
  )
}

function StudyNext({ studyNext }: { studyNext: DashboardProps["study_next"] }) {
  const intl = useIntl()
  if (studyNext.areas.length === 0) return null
  return (
    <section className="mt-10">
      <h2>{intl.formatMessage({ id: "dashboard.next_title" })}</h2>
      <p className="mt-1 text-sm text-ink-muted">
        {studyNext.basis === "weakest"
          ? intl.formatMessage({ id: "dashboard.next_weakest" })
          : intl.formatMessage({ id: "dashboard.next_blueprint" })}
      </p>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        {studyNext.areas.map((area) => {
          const links = [
            ...area.quizzes.map((q) => ({ ...q, href: `/quizzes/${q.id}`, icon: ListChecks, itemId: "dashboard.item_quiz" })),
            ...area.decks.map((d) => ({ ...d, href: `/decks/${d.id}`, icon: Layers, itemId: "dashboard.item_deck" })),
          ]
          return (
            <div key={area.id} className="rounded-xl border border-hairline bg-page p-4">
              <h3>{area.name}</h3>
              {area.percent !== null && (
                <p className="mt-1 text-sm text-ink-muted">{intl.formatMessage({ id: "dashboard.percent_so_far" }, { percent: area.percent })}</p>
              )}
              {links.length === 0 ? (
                <p className="mt-2 text-sm">
                  <FormattedMessage
                    id="dashboard.no_material"
                    values={{
                      practice: (chunks) => <Link href="/practice_exams">{chunks}</Link>,
                      assistant: (chunks) => <Link href="/assistant">{chunks}</Link>,
                    }}
                  />
                </p>
              ) : (
                <ul className="mt-3 space-y-1 p-0">
                  {links.map((l) => (
                    <li key={`${l.itemId}-${l.id}`} className="list-none">
                      <Link href={l.href} className="inline-flex items-center gap-2 text-sm">
                        <l.icon className="h-4 w-4" /> {l.title}
                        <span className="text-ink-muted">{intl.formatMessage({ id: l.itemId }, { count: l.tagged })}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}

